import { toast } from "sonner";

const BASE_URL = import.meta.env.VITE_API_URL || "";

/** The full address of an API path (the API lives on its own address in production, so a bare "/api/..." would reach the web host). */
export const apiUrl = (path: string): string => `${BASE_URL}${path}`;

/**
 * Wrap fetch to convert network failures (no connection, DNS errors, etc.)
 * into a friendlier error message instead of "Failed to fetch".
 */
async function fetchWithOfflineMessage(
  url: string,
  init?: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    // Most likely the user is offline or the server is unreachable.
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new Error("You're offline. Changes will sync when you reconnect.");
    }
    throw new Error(
      "Can't reach the server. Check your connection and try again."
    );
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    toast.error("Session expired. Please log in again.");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const errorBody = body as { error?: unknown; details?: string[]; fields?: Record<string, string[]>; success?: boolean };
    let message: string;
    if (typeof errorBody.error === "string") {
      message = errorBody.error;
    } else if (errorBody.error && typeof errorBody.error === "object") {
      // Hono zod-validator returns { success: false, error: { issues: [...] } }
      const zodErr = errorBody.error as { issues?: { message: string }[] };
      if (Array.isArray(zodErr.issues) && zodErr.issues.length > 0) {
        message = zodErr.issues.map((i) => i.message).join(", ");
      } else {
        message = `Request failed with status ${response.status}`;
      }
    } else if (typeof (body as any).message === "string") {
      message = (body as any).message;
    } else {
      message = `Request failed with status ${response.status}`;
    }
    if (errorBody.details && Array.isArray(errorBody.details)) {
      message += ": " + errorBody.details.join(", ");
    }
    if (errorBody.fields) {
      const fieldErrors = Object.entries(errorBody.fields)
        .map(([k, v]) => `${k}: ${(v as string[]).join(", ")}`)
        .join("; ");
      message += ": " + fieldErrors;
    }
    // The answer is kept on the error for the few callers that need more than the message (for example a list of duplicates)
    throw Object.assign(new Error(message), { status: response.status, data: body });
  }

  return response.json() as Promise<T>;
}

export async function apiGet<T>(url: string): Promise<T> {
  const response = await fetchWithOfflineMessage(`${BASE_URL}${url}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return handleResponse<T>(response);
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const response = await fetchWithOfflineMessage(`${BASE_URL}${url}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(response);
}

export async function apiPut<T>(url: string, body: unknown): Promise<T> {
  const response = await fetchWithOfflineMessage(`${BASE_URL}${url}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(response);
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const response = await fetchWithOfflineMessage(`${BASE_URL}${url}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(response);
}

export async function apiDelete(url: string, body?: unknown): Promise<void> {
  const response = await fetchWithOfflineMessage(`${BASE_URL}${url}`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    toast.error("Session expired. Please log in again.");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message =
      (body as { error?: string }).error ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }
}

/** Check if the browser reports being online. */
export function isOnline(): boolean {
  return navigator.onLine;
}
